/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { cn } from '@/shared/ui/utils/tailwind';
import { Text, NativeInput } from '@/shared/ui';
import { SegmentedControl } from '@/shared/ui/components/basic/SegmentedControl';
import { formatStoredNumber, parseMaskedNumber } from '@/shared/utils/number-format';

const BEDROOM_ITEMS = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5+' },
] as const;

interface BedroomSelectorProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

/**
 * Renders a bedroom-count selector as a canonical SegmentedControl (1..5+),
 * where clicking the active value clears the selection.
 *
 * @param props - The field label, current value, and change callback.
 * @returns The bedroom selector control.
 */
export function BedroomSelector({ label, value, onChange }: BedroomSelectorProps) {
  const handleChange = (v: string) => onChange(value === v ? '' : v);

  return (
    <div className="flex flex-col gap-[var(--space-1)]">
      <Text component="label" size="xs" fw={500} className="block mb-[var(--space-1)] text-[var(--text-secondary)] tracking-wide"
        style={{ fontFamily: 'var(--font-family)' }}
      >
        {label}
      </Text>
      <div className="flex items-center min-h-[44px]">
        <SegmentedControl
          value={value || ''}
          onChange={handleChange}
          items={BEDROOM_ITEMS as unknown as { value: string; label: string }[]}
          variant="solid"
          size="md"
          ariaLabel={label}
        />
      </div>
    </div>
  );
}

interface CurrencyInputProps {
  id: string;
  label: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
}

/**
 * Currency text field with an R$ prefix that masks/parses the input into a
 * numeric value (or undefined when empty).
 *
 * @param props - The input id, label, current numeric value, and change
 *   callback.
 * @returns The masked currency input.
 */
export function CurrencyInput({ id, label, value, onChange }: CurrencyInputProps) {
  const displayValue = formatStoredNumber(value ?? null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    onChange(parseMaskedNumber(e.target.value));
  };

  return (
    <div className="flex flex-col gap-[var(--space-1)]">
      <Text
        component="label"
        size="xs"
        fw={500}
        className="block mb-[var(--space-1)] text-[var(--text-secondary)] tracking-wide"
        style={{ fontFamily: 'var(--font-family)' }}
        htmlFor={id}
      >
        {label}
      </Text>
      <div className={cn(
        'flex items-center gap-[var(--space-2)] rounded-(--radius-md)',
        'border border-[var(--border-subtle)] bg-[var(--surface-secondary)]',
        'px-[var(--space-3)] min-h-[44px]',
        'focus-within:ring-2 focus-within:ring-[var(--input-focus-ring)]',
        'transition-shadow duration-150',
      )}>
        <Text component="span" size="sm" className="text-[var(--text-tertiary)] select-none shrink-0">
          R$
        </Text>
        <NativeInput
          id={id}
          type="text"
          inputMode="numeric"
          value={displayValue}
          onChange={handleChange}
          placeholder="0,00"
          className={cn(
            'w-full bg-transparent text-sm text-[var(--text-primary)] outline-none',
            'placeholder:text-[var(--text-tertiary)]',
          )}
          style={{ fontFamily: 'var(--font-family)' }}
        />
      </div>
    </div>
  );
}
